import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core'
import { BehaviorSubject, Subject } from 'rxjs'
import { mergeMap, take, takeUntil } from 'rxjs/operators'
import { fadeIn } from '../../../animations/fadeIn'
import { slideInOnlyUpStaggered } from '../../../animations/slideInOnlyUpStaggered'
import { CylindersDisplayColumns, OrderHistoryDisplayColumns } from '../../../constants/keying.constants'
import { Cylinder, KeySystem, Operator, ShearLines, Structure } from '../../../models/dev.test.models'
import { KeywayService, MasterKey, SelectedMasterKeyService } from '@overtur-monorepo/keying-hierarchy'
import { CylinderDataSets, KeyingCylinderService } from '../../../services/keying.cylinder.service'
import { KeyingSystemService } from '../../../services/keying.system.service'
import { KeyingStructureService } from '../../../services/keying.structure.service'
import { LoadingService } from '../../../services/loading.service'
import { FormatDataService } from '../../../services/format.data.service'
import { CombinatingRuleSets, KeyingDataSetsService } from '../../../services/keying.data.sets.service'
import { LoggingService } from '../../../services/logging.service'
import { ErrorService } from '../../../services/error.service'
import { CylinderValidationItem, KeySystemService, ValidateCylindersRequest } from '../../../services/key-system/key-system.service'
import { LibraryItemData } from '../library-list-item/library-list-item.component'
import { KeyNodeType } from '@overtur-monorepo/keying-shared'
import moment from 'moment'
import { FormArray, FormControl, FormGroup } from '@angular/forms'
import { MasterKeyService } from '../../../services/keying.masterkey.service'

const CylinderType = {
  UnverifiedCylinder: 'Unverified Cylinders',
  SelectedCylinders: 'Selected Cylinders',
  AllCylinders: 'All Cylinders',
  CrossKeyedCylinder: 'CrossKeyed Cylinders',
}

@Component({
  selector: 'app-cylinder-library',
  templateUrl: './cylinder-library.component.html',
  styleUrls: ['./cylinder-library.component.scss'],
  animations: [fadeIn, slideInOnlyUpStaggered],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CylinderLibraryComponent implements OnInit, OnDestroy, OnChanges {
  destroy$ = new Subject()
  keyNodeType = KeyNodeType
  // @ViewChildren(MultiSelectionCheckboxDirective) checkboxs: QueryList<MultiSelectionCheckboxDirective>
  checkboxesRef = undefined

  cylindersDisplayColumns = CylindersDisplayColumns
  orderHistoryDisplayColumns = OrderHistoryDisplayColumns
  combinatingRuleSets: CombinatingRuleSets
  filteredCylinders = new BehaviorSubject<Array<Operator>>([])
  filteredOrderHistory = new BehaviorSubject<Array<any>>([])
  selectedMasterKey: MasterKey
  keySystem: KeySystem
  structure: Structure
  cylinderCounter = 0
  selectedCylinder: Cylinder
  flatShearlineDepths
  cylinderDataSets: CylinderDataSets
  filteredTypeCylinder = CylinderType.UnverifiedCylinder
  validateCylindersDisabled = true

  allCylinders = []
  _filteredAllCylinders = []
  filteredCylinderTypeCylinders = []
  filteredMasterKeyCylinders = []
  killSearch = false
  CylinderType = CylinderType
  filteredList
  allKeyWaysFromFamily
  searchTerm = ''
  isLoadingCylinders = false
  private cylinderLoadInProgress = false

  // Order for masterKeyTypeId sorting: 2, 4, 9, 11, 6, 8, 10, then rest
  private masterKeyTypeIdOrder = [2, 4, 9, 11, 6, 8, 10]

  get filteredAllCylinders() {
    return this._filteredAllCylinders
  }

  set filteredAllCylinders(value: any[]) {
    this._filteredAllCylinders = value || []
    const { data } = this.masterKeyService.masterKeyUpdatedSubject.value
    if (data?.action === 'cross') {
      const cyl = this._filteredAllCylinders.find((cyl) => cyl.name == data?.name)
      if (cyl) {
        this.selectCylinder(cyl)
        this.masterKeyService.masterKeyUpdatedSubject.next({})
      }
    }
  }

  count = 0
  editing = {
    operators: false,
    shearLine: false,
  }

  clickedOnCylinderNav = false

  @Input() cylinderValidation
  @Input() cylinderNav
  @Input() hasControlKeyMaster: boolean
  @Input() selectedTabId: number

  @Output() selectedCylinderUpdate = new EventEmitter<any>()
  @Output() prepareForMECS = new EventEmitter<boolean>()

  orderHistory: Array<any> = []

  group = new FormGroup({
    skills: new FormArray(this.filteredAllCylinders.map((s) => new FormControl(false))),
  })

  get skills() {
    const skills = this.group.get('skills') as FormArray
    switch (this.filteredTypeCylinder) {
      case CylinderType.AllCylinders:
      case CylinderType.UnverifiedCylinder:
        return skills?.controls
      case CylinderType.SelectedCylinders:
        return skills.controls.filter((cylinder) => cylinder.value)
      default:
        break
    }
  }

  get selectedCylinders() {
    return this.filterCylindersByCylinderType(CylinderType.SelectedCylinders)
  }

  constructor(
    private selectedMasterKeyService: SelectedMasterKeyService,
    private keyingCylinderService: KeyingCylinderService,
    private keyingSystemService: KeyingSystemService,
    private formatDataService: FormatDataService,
    private keyingStructureService: KeyingStructureService,
    private loadingService: LoadingService,
    private loggingService: LoggingService,
    private keyingDataSetsService: KeyingDataSetsService,
    private errorService: ErrorService,
    private keywayService: KeywayService,
    private masterKeyService: MasterKeyService,
    private keySystemService: KeySystemService,
    private cdr: ChangeDetectorRef
  ) {}

  lastCheckboxIndex = -1
  selectAllCheckbox = false
  selectedCylindersCount = 0
  copiedCylinderId: number | null = null

  /* istanbul ignore next */
  copySetList(event: MouseEvent, cylinder: any) {
    event.stopPropagation()
    ;(event.currentTarget as HTMLElement).blur()
    return navigator.clipboard.writeText(cylinder.setlist).then(() => {
      this.copiedCylinderId = cylinder.id
      this.cdr.markForCheck()
      setTimeout(() => {
        this.copiedCylinderId = null
        this.cdr.markForCheck()
      }, 1500)
    })
  }

  displayAnnotations(cylinder: any) {
    return cylinder.annotations?.map((annotation) => annotation?.annotation).join(', ')
  }

  getSortedOperatorRange(cylinder: any): any[] {
    if (!cylinder?.operatorRange || cylinder.operatorRange.length === 0) {
      return []
    }

    return [...cylinder.operatorRange].sort((a, b) => {
      const indexA = this.masterKeyTypeIdOrder.indexOf(a.masterKeyTypeId)
      const indexB = this.masterKeyTypeIdOrder.indexOf(b.masterKeyTypeId)

      // If both are in the order array, sort by their position
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB
      }
      // If only a is in the order array, it comes first
      if (indexA !== -1) {
        return -1
      }
      // If only b is in the order array, it comes first
      if (indexB !== -1) {
        return 1
      }
      // If neither is in the order array, maintain original order
      return 0
    })
  }

  getOperatorRangeClass(masterKeyTypeId: number): string {
    // Change keys: DirectChangeMaster(7), Direct(8), ChangeKey(10), ChangeKeyThruNode(99)
    if ([7, 8, 10, 99].includes(masterKeyTypeId)) {
      return 'operator-change-key'
    }
    // SKD: SingleKeyDifferentMaster(5), SingleKeyDifferent(6), SKDThruNode(100)
    if ([5, 6, 100].includes(masterKeyTypeId)) {
      return 'operator-skd'
    }
    // Selective Master(11)
    if (masterKeyTypeId === 11) {
      return 'operator-slm'
    }
    // Default: Master(9) and others
    return 'operator-default'
  }

  trackByOperator(index: number, op: any): number | string {
    return op.id ?? op.displayText
  }

  getOperatorTypeTooltip(masterKeyTypeId: number): string {
    const tooltips: Record<number, string> = {
      2: 'Control Key',
      4: 'Construction Key',
      6: 'Single Key Different',
      8: 'Direct',
      9: 'Master',
      10: 'Change Key',
      11: 'Selective Master',
      17: 'Construction Key Insert',
    }
    return tooltips[masterKeyTypeId] || 'Unknown'
  }

  getCylinderTypeTooltip(cylinder: any): string {
    return cylinder?.isDefaultCylinder ? 'Default Cylinder' : 'Versioned Cylinder'
  }

  canVerifyCylinder(cylinder: any) {
    return cylinder.annotations?.some(
      (annotation) => annotation.annotation === 'Has default key in elevated KeySection'
    )
  }

  verifyCylinder(masterKeyId, cylinderId) {
    this.keyingCylinderService
      .verifyCylinder(this.structure.id, this.keySystem.id, masterKeyId, cylinderId)
      .pipe(take(1))
      .subscribe((result) => {
        this.goToLibrary()
        this.clearCylindersSelection()
        this.keyingDataSetsService.loadStructureDetails(this.structure.id)
        this.cdr.markForCheck()
      })
  }

  /* istanbul ignore next */
  onClickAllCheckbox() {
    this.lastCheckboxIndex = -1
    this.toggleCheckbox(0, this.filteredCylinderTypeCylinders.length, this.selectAllCheckbox)
    this.selectedCylindersCount = this.selectAllCheckbox ? this.filteredCylinderTypeCylinders.length : 0
    this.cdr.markForCheck()
  }

  /* istanbul ignore next */
  toggleCheckbox(start: number, end: number, selected: boolean) {
    this.filteredCylinderTypeCylinders.slice(Math.min(start, end), Math.max(start, end) + 1).forEach((cyl) => {
      cyl.selected = selected
    })
  }

  /* istanbul ignore next */
  onClickCheckbox(evt: MouseEvent, cylinder: any, index: number) {
    if (evt.shiftKey && this.lastCheckboxIndex >= 0) {
      this.toggleCheckbox(
        this.lastCheckboxIndex,
        index,
        this.filteredCylinderTypeCylinders[this.lastCheckboxIndex].selected
      )
    } else {
      this.lastCheckboxIndex = index
      cylinder.selected = cylinder.selected ? false : true
    }
    this.selectedCylindersCount = this.filterCylindersByCylinderType(CylinderType.SelectedCylinders).length
    this.cdr.markForCheck()
  }

  /* istanbul ignore next */
  ngOnInit(): void {
    this.keyingSystemService.keySystem$
      .pipe(
        takeUntil(this.destroy$),
        mergeMap((keySystem: KeySystem) => {
          if (!keySystem) {
            return
          }
          this.keySystem = keySystem
          this.cdr.markForCheck()

          return this.keyingStructureService.structure$
        })
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe((structure: Structure) => {
        this.structure = structure
        this.cdr.markForCheck()
      })

    this.selectedMasterKeyService.selectedMasterKey$
      .pipe(takeUntil(this.destroy$))
      .subscribe((masterKey: MasterKey) => {
        if (!masterKey) {
          return
        }

        if (this.selectedMasterKey) {
          setTimeout(() => {
            this.goToLibrary()
          }, 10)
        }

        // When switching AWAY FROM a CrossKeyChild node, reset the filter back to
        // UnverifiedCylinder. The CrossKeyedCylinder filter is auto-applied by getAllCyls
        // for CrossKeyChild nodes and carries no meaning for regular master keys.
        //
        // Critically, we check the PREVIOUS key (this.selectedMasterKey) rather than
        // the current filteredTypeCylinder value. This preserves the user's manually
        // chosen filter when they navigate between regular master key nodes — e.g. a
        // user who explicitly picks "CrossKeyed Cylinders" on node A and then switches
        // to node B (both non-CrossKeyChild) should keep that filter on B.
        if (
          this.selectedMasterKey?.masterKeyTypeId === KeyNodeType.CrossKeyChild &&
          masterKey.masterKeyTypeId !== KeyNodeType.CrossKeyChild
        ) {
          this.filteredTypeCylinder = CylinderType.UnverifiedCylinder
        }

        const selectedMasterKey = this.selectedMasterKey
        this.selectedMasterKey = masterKey

        this.killSearch = true
        setTimeout(() => {
          this.killSearch = false
          this.cdr.markForCheck()
        })
        this.cdr.markForCheck()
      })

    // Cylinders
    this.keyingCylinderService.cylinderDataSets$
      .pipe(takeUntil(this.destroy$))
      .subscribe((cylinderDataSets: CylinderDataSets) => {
        if (!cylinderDataSets) {
          return
        }
        this.cylinderDataSets = cylinderDataSets
        this.cylinderCounter = this.cylinderDataSets.cylinders.length

        if (this.cylinderDataSets.cylinders.length > 0) {
          let flat = this.selectedMasterKey.tmk().squashIncludeChangeKeys()
          let flatFiltered = flat.filter((mk) => !!mk)
          this.cylinderDataSets.cylinders.map((cyl) => (cyl = this.formatOperators(cyl, flatFiltered)))
        }

        this.filteredMasterKeyCylinders = this.cylinderDataSets.cylinders
        this.cdr.markForCheck()
      })

    // Selected Cylinder - only react to non-null values to avoid triggering goToLibrary on init
    this.keyingCylinderService.selectedCylinder$
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedCylinder: Cylinder) => {
        if (selectedCylinder) {
          this.selectCylinder(selectedCylinder)
        }
      })

    // Combinating Rules
    this.keyingDataSetsService.combinatingRuleSets$
      .pipe(takeUntil(this.destroy$))
      .subscribe((combinatingRuleSets: CombinatingRuleSets) => {
        this.combinatingRuleSets = combinatingRuleSets
        this.cdr.markForCheck()
      })

    // KeyWays
    this.keywayService
      .getAllKeyways(this.structure.keywayFamilyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((keyways) => {
        this.allKeyWaysFromFamily = keyways
        this.cdr.markForCheck()
      })

    this.generateShearlinesAndPinning()
    this.notifyDuplicateSymbols()
  }

  /* istanbul ignore next */
  ngOnChanges(changes: SimpleChanges) {
    if (changes.cylinderNav.currentValue === true) {
      this.clickedOnCylinderNav = true
      this.goToLibrary()
      this.cdr.markForCheck()
    }
  }

  /* istanbul ignore next */
  ngOnDestroy() {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  /* istanbul ignore next */
  clearCylindersSelection() {
    ;(this.group.get('skills') as FormArray).controls
      .filter((control) => control.value)
      .forEach((control) => control.patchValue(false))
  }

  /* istanbul ignore next */
  clickStopper(event) {
    event.stopPropagation()
    return
  }

  /* istanbul ignore next */
  selectNewCylinder(newCylinder) {
    const loadingRef = this.loadingService.loading$.subscribe((loading) => {
      if (loading.isLoading === false) {
        this.selectCylinder(newCylinder)
        loadingRef.unsubscribe()
      }
    })
  }

  /* istanbul ignore next */
  editOperators() {
    this.editing = {
      operators: true,
      shearLine: false,
    }
  }

  /* istanbul ignore next */
  editShearLine() {
    this.editing = {
      operators: false,
      shearLine: true,
    }
  }

  /* istanbul ignore next */
  convertToListItem(cylinder) {
    let newLibraryItemData = new LibraryItemData()
    if (!cylinder.keywayName) {
      cylinder.keywayName = 'N/A'
    }
    newLibraryItemData.columns.push(
      { label: 'Cylinder', data: cylinder.name },
      { label: 'Keyway', data: cylinder.keywayName },
      { label: 'Order Number', data: 'N/A' },
      { label: 'Operated By', data: cylinder.oppString }
    )

    return newLibraryItemData
  }

  /* istanbul ignore next */
  shearLineChange(isSaveShearline = false) {
    this.selectedCylinder.formattedShearLines = this.formatDataService.auditShearLines(
      this.selectedCylinder.formattedShearLines,
      this.combinatingRuleSets.combinatingRules
    )
    this.prepareCylinderRequest(isSaveShearline)
  }

  validateAndSaveShearline() {
    this.shearLineChange(true)
  }

  /* istanbul ignore next */
  onSortShearLines() {
    this.selectedCylinder.formattedShearLines = this.formatDataService.auditShearLines(
      this.selectedCylinder.formattedShearLines,
      this.combinatingRuleSets.combinatingRules
    )
    this.sortShearLinesRequest()
  }

  /* istanbul ignore next */
  onResetShearLines() {
    this.selectedCylinder.formattedShearLines = this.formatDataService.auditShearLines(
      this.selectedCylinder.formattedShearLines,
      this.combinatingRuleSets.combinatingRules
    )
    this.resetShearLinesRequest()
  }

  /* istanbul ignore next */
  onGenerateShearLines() {
    this.generateCrossKeyedShearLinesRequest()
    this.editShearLine()
  }

  /* istanbul ignore next */
  addRow() {
    this.selectedCylinder = this.keyingCylinderService.addRow(this.selectedCylinder)
    this.prepareCylinderRequest()
  }

  /* istanbul ignore next */
  prepareCylinderRequest(isSaveShearline = false) {
    this.flatShearlineDepths = this.selectedCylinder.formattedShearLines.flat()
    this.selectedCylinder.shearLines.shearLineDepths = this.flatShearlineDepths
    this.selectedCylinderUpdate.emit(this.selectedCylinder)
    this.loadingService.set({ isLoading: true, message: 'Adding Cylinder ShearLines' })
    this.keyingCylinderService
      .postCylinderShearLines(
        this.structure.id,
        this.selectedCylinder.keySystemId,
        this.selectedCylinder.masterKeyId,
        this.selectedCylinder.id,
        false,
        this.selectedCylinder.shearLines
      )
      .pipe(take(1))
      .subscribe(
        (res: ShearLines) => {
          if (!this.selectedCylinder) {
            return
          }
          this.loadingService.set({ isLoading: false, message: '' })
          this.selectedCylinder.shearLines = res
          this.selectedCylinder.formattedShearLines = this.formatDataService.formatShearLines(
            res,
            this.combinatingRuleSets.combinatingRules
          )
          if (isSaveShearline) {
            this.keyingCylinderService.saveShearlines(res)
          }
          this.cdr.markForCheck()
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
          this.cdr.markForCheck()
        }
      )
  }

  /* istanbul ignore next */
  sortShearLinesRequest() {
    this.flatShearlineDepths = this.selectedCylinder.formattedShearLines.flat()
    this.selectedCylinder.shearLines.shearLineDepths = this.flatShearlineDepths
    this.selectedCylinderUpdate.emit(this.selectedCylinder)
    this.loadingService.set({ isLoading: true, message: 'Sorting Shear Line' })
    this.keyingCylinderService
      .sortShearLines(
        this.structure.id,
        this.selectedCylinder.keySystemId,
        this.selectedCylinder.masterKeyId,
        this.selectedCylinder.id,
        this.selectedCylinder.shearLines
      )
      .pipe(take(1))
      .subscribe(
        (res: ShearLines) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.selectedCylinder.shearLines = res
          this.selectedCylinder.formattedShearLines = this.formatDataService.formatShearLines(
            res,
            this.combinatingRuleSets.combinatingRules
          )
          this.cdr.markForCheck()
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
          this.cdr.markForCheck()
        }
      )
  }

  /* istanbul ignore next */
  resetShearLinesRequest() {
    this.flatShearlineDepths = this.selectedCylinder.formattedShearLines.flat()
    this.selectedCylinder.shearLines.shearLineDepths = this.flatShearlineDepths
    this.selectedCylinderUpdate.emit(this.selectedCylinder)
    this.loadingService.set({ isLoading: true, message: 'Resetting Shear Line' })
    this.keyingCylinderService
      .resetShearLines(
        this.structure.id,
        this.selectedCylinder.keySystemId,
        this.selectedCylinder.masterKeyId,
        this.selectedCylinder.id
      )
      .pipe(take(1))
      .subscribe(
        (res: ShearLines) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.selectedCylinder.shearLines = res
          this.selectedCylinder.formattedShearLines = this.formatDataService.formatShearLines(
            res,
            this.combinatingRuleSets.combinatingRules
          )
          this.cdr.markForCheck()
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
          this.cdr.markForCheck()
        }
      )
  }

  /* istanbul ignore next */
  generateCrossKeyedShearLinesRequest() {
    this.flatShearlineDepths = this.selectedCylinder.formattedShearLines.flat()
    this.selectedCylinder.shearLines.shearLineDepths = this.flatShearlineDepths
    this.selectedCylinderUpdate.emit(this.selectedCylinder)
    this.loadingService.set({ isLoading: true, message: 'Generating Cross Keyed Shear Line' })
    this.keyingCylinderService
      .generateShearLines(
        this.structure.id,
        this.selectedCylinder.keySystemId,
        this.selectedCylinder.masterKeyId,
        this.selectedCylinder.id
      )
      .pipe(take(1))
      .subscribe(
        (res: ShearLines) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.selectedCylinder.shearLines = res
          this.selectedCylinder.formattedShearLines = this.formatDataService.formatShearLines(
            res,
            this.combinatingRuleSets.combinatingRules
          )
          this.cdr.markForCheck()
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
          this.cdr.markForCheck()
        }
      )
  }

  /* istanbul ignore next */
  updateSearch() {}

  /**
   * Filters cylinders by search term while preserving hierarchical grouping
   *
   * IMPORTANT: This method uses hierarchicalFilter() to maintain the parent-child relationship
   * between default cylinders and their versioned cylinders. Any changes to this logic must
   * ensure that:
   * 1. When a default cylinder matches search → include ALL its versioned cylinders
   * 2. When a versioned cylinder matches search → include its parent default cylinder
   * 3. Results are re-sorted using sortHierarchically() to maintain grouping:
   *    Default → Versioned → Default → Versioned (NOT all defaults then all versioned)
   *
   * @param on - Search term entered by user
   */
  /* istanbul ignore next */
  filter(on) {
    this.searchTerm = on?.toUpperCase() || ''

    // Apply hierarchical filter then sort to maintain default→versioned grouping
    let flat = this.selectedMasterKey?.tmk()?.squashIncludeChangeKeys() || []

    this.filteredAllCylinders = this.hierarchicalFilter(this.allCylinders)
    if (this.filteredAllCylinders.length > 0 && flat.length > 0) {
      this.filteredAllCylinders = this.sortHierarchically(this.filteredAllCylinders, flat, false)
    }

    this.filteredCylinderTypeCylinders = this.hierarchicalFilter(
      this.filterCylindersByCylinderType(this.filteredTypeCylinder)
    )
    if (this.filteredCylinderTypeCylinders.length > 0 && flat.length > 0) {
      this.filteredCylinderTypeCylinders = this.sortHierarchically(this.filteredCylinderTypeCylinders, flat, false)
    }

    if (this.cylinderDataSets?.cylinders) {
      this.filteredMasterKeyCylinders = this.hierarchicalFilter(this.cylinderDataSets.cylinders)
      if (this.filteredMasterKeyCylinders.length > 0 && flat.length > 0) {
        let flatFiltered = flat.filter((mk) => !!mk)
        this.filteredMasterKeyCylinders = this.sortHierarchically(this.filteredMasterKeyCylinders, flatFiltered, true)
      }
    }

    this.cdr.markForCheck()
  }

  /**
   * Filters cylinders while preserving parent-child relationships
   *
   * CRITICAL: Cylinder hierarchy logic explained:
   * - Default cylinder: cylinder.defaultCylinderId is null/undefined
   * - Versioned cylinder: cylinder.defaultCylinderId contains the parent cylinder's ID
   *
   * Search behavior:
   * 1. If no search term → return all cylinders with unhighlighted names
   * 2. If search term present:
   *    a) Find all cylinders where name includes search term
   *    b) For each matched DEFAULT cylinder (defaultCylinderId is null):
   *       - Include the default cylinder itself
   *       - Include ALL versioned cylinders where defaultCylinderId === default.id
   *    c) For each matched VERSIONED cylinder (defaultCylinderId has value):
   *       - Include the versioned cylinder itself
   *       - Include its parent default cylinder (where id === versioned.defaultCylinderId)
   *
   * This ensures when user searches:
   * - "CYL-001" (default) → shows CYL-001 + CYL-001-V1 + CYL-001-V2
   * - "CYL-001-V1" (versioned) → shows CYL-001-V1 + CYL-001 (parent)
   *
   * WARNING: Do NOT change this to simple name filtering or the UI grouping will break!
   *
   * @param cylinders - Array of cylinders to filter
   * @returns Filtered array with hierarchical relationships preserved
   */
  /* istanbul ignore next */
  private hierarchicalFilter(cylinders: any[]): any[] {
    if (!this.searchTerm) {
      return cylinders.map((c) => ({ ...c, highlightedName: c.name }))
    }

    const cylindersToInclude = new Set<number>()

    // First pass: find all cylinders matching the search term
    const matchedCylinders = cylinders.filter((c) => c.name.includes(this.searchTerm))

    matchedCylinders.forEach((matched) => {
      cylindersToInclude.add(matched.id)

      if (!matched.defaultCylinderId) {
        // This is a default cylinder - include all its versioned cylinders
        cylinders.forEach((c) => {
          if (c.defaultCylinderId === matched.id) {
            cylindersToInclude.add(c.id)
          }
        })
      } else {
        // This is a versioned cylinder - include its parent default cylinder
        cylindersToInclude.add(matched.defaultCylinderId)
      }
    })

    // Return filtered cylinders with highlighted names
    return cylinders
      .filter((c) => cylindersToInclude.has(c.id))
      .map((c) => ({ ...c, highlightedName: this.getHighlightedName(c.name) }))
  }

  /**
   * Highlights search term in cylinder name using HTML <mark> tags
   *
   * SECURITY NOTE: The search term is escaped before being used in a regex to prevent:
   * 1. Regex injection (user typing special chars like ( [ * + causes crashes)
   * 2. Invalid regex patterns breaking the search
   *
   * Example: User searches "CYL-001(" → escaped to "CYL-001\\(" → safe regex
   *
   * @param text - Cylinder name to highlight
   * @returns Cylinder name with search term wrapped in <mark> tags
   */
  private getHighlightedName(text: string): string {
    if (!this.searchTerm || !text) {
      return text
    }
    // Escape special regex characters to prevent crashes
    const escapedTerm = this.searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const regex = new RegExp(`(${escapedTerm})`, 'gi')
    return text.replace(regex, '<mark class="search-highlight">$1</mark>')
  }

  /* istanbul ignore next */
  selectCylinder(cylinder) {
    if (!cylinder) {
      return this.goToLibrary()
    }
    this.count = this.count + 1
    this.selectedCylinder = cylinder
    this.loadingService.set({ isLoading: true, message: 'Getting Cylinder' })
    // https://localhost:44363/v2/structures/2510/keySystems/2518/Cylinders/MasterKeys/27523
    this.keyingCylinderService
      .getCylinder(
        this.structure.id,
        this.selectedCylinder.keySystemId,
        this.selectedCylinder.masterKeyId,
        this.selectedCylinder.id
      )
      .pipe(take(1))
      .subscribe(
        (resCylinder) => {
          this.selectedCylinder = resCylinder
          if (!this.selectedCylinder.requiredPinningGeneration) this.prepareForMECSHandler()
          this.selectedCylinder.formattedShearLines = this.formatDataService.formatShearLines(
            this.selectedCylinder.shearLines,
            this.combinatingRuleSets.combinatingRules
          )

          this.addKeywaysToOperators(() => {
            this.selectedCylinderUpdate.emit(this.selectedCylinder)
            this.loggingService.log('Operators:', this.selectedCylinder.operators)
            this.filteredCylinders.next(this.selectedCylinder.operators)

            // TODO
            this.filteredOrderHistory.next(this.orderHistory)

            if (resCylinder.shearLines && resCylinder.shearLines.shearLineDepths.length > 0) {
              this.prepareCylinderRequest()
            }
            this.cdr.markForCheck()
          })
          this.loadingService.set({ isLoading: false, message: '' })
          this.cdr.markForCheck()
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
          this.cdr.markForCheck()
        }
      )
  }

  addKeywaysToOperators(callback: Function) {
    const flat = this.keySystem.masterKeysKeying.squashIncludeChangeKeys()

    let operatorCount = this.selectedCylinder.operators.length

    this.selectedCylinder.operators.map((operator) => {
      const found = flat.find((mk) => operator.name === mk.keySetSymbol)

      if (found?.keywayId) {
        this.allKeyWaysFromFamily.map((keyway) => {
          if (keyway.id === found?.keywayId) {
            operatorCount--

            const cylinder = flat.find((m) => m.keySetSymbol === operator.name)
            operator.keywayName = cylinder?.keywayId ? keyway.name : '[N/A]'
            operator.keywayId = keyway.id

            if (operatorCount === 0) {
              callback()
            }
          }
        })
      } else {
        operator.keywayName = '[N/A]'
        operatorCount--

        if (operatorCount === 0) {
          callback()
        }
      }
      operator.bitting = operator.bitting ? operator.bitting : '[N/A]'
    })
  }

  /* istanbul ignore next */
  goToLibrary(isNewKeyAdded = false) {
    this.childCylinders()
    this.selectedCylinder = null
    this.cylinderValidation = null
    this.selectedCylinderUpdate.emit(null)

    if (isNewKeyAdded) {
      this.masterKeyService.getMasterKeys(this.structure.id, this.keySystem.id, false).subscribe((masterKeysKeying) => {
        let keySystem = { ...this.keySystem }
        keySystem.masterKeysKeying = new MasterKey(masterKeysKeying)
        this.keyingSystemService.selectKeySystem(keySystem)
        this.keyingSystemService
          .getKeySystems(this.structure.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe((keySystems: any[]) => {
            this.structure.keySystems = keySystems
            const matchingKeySystem = keySystems.find((m) => m.id === this.keySystem.id)
            if (matchingKeySystem) {
              let modifiedMatchingKeySystem = { ...matchingKeySystem }
              modifiedMatchingKeySystem.masterKeysKeying = this.keySystem.masterKeysKeying
              this.keyingSystemService.selectKeySystem(modifiedMatchingKeySystem)
            }
            this.cdr.markForCheck()
          })
        this.cdr.markForCheck()
      })
    }
  }

  /* istanbul ignore next */
  childCylinders() {
    this.allCylinders = []
    this.filteredAllCylinders = []
    this.filteredCylinderTypeCylinders = []
    let flat = this.selectedMasterKey.squashIncludeChangeKeys()
    this.filteredList = flat.filter(
      (mk) =>
        // mk.id !== this.selectedMasterKey.id &&
        (mk.masterKeyTypeId === this.keyNodeType.Master ||
          mk.masterKeyTypeId === this.keyNodeType.ChangeKey ||
          mk.masterKeyTypeId === this.keyNodeType.SingleKeyDifferent ||
          mk.masterKeyTypeId === this.keyNodeType.CrossKeyChild ||
          mk.masterKeyTypeId === this.keyNodeType.Direct) &&
        (mk.isAssigned || mk.masterKeyTypeId === this.keyNodeType.CrossKeyChild)
    )

    this.getAllCyls(this.filteredList)
  }

  /* istanbul ignore next */
  sortAlphaNum = (a, b) => a.name.localeCompare(b.name, 'en', { numeric: true })

  /**
   * Sorts cylinders hierarchically: Default → Versioned → Default → Versioned
   *
   * CRITICAL: This method is called AFTER hierarchicalFilter() in the search flow
   * to maintain proper visual grouping of default and versioned cylinders.
   *
   * Algorithm:
   * 1. Separates cylinders into two groups:
   *    - Default cylinders (isDefaultCylinder = true)
   *    - Versioned cylinders grouped by their defaultCylinderId
   * 2. Sorts default cylinders alphabetically by name
   * 3. For each default cylinder, outputs:
   *    - The default cylinder
   *    - All its versioned cylinders (sorted alphabetically)
   *
   * Example output order:
   *   CYL-001 (default)
   *   CYL-001-V1 (versioned, defaultCylinderId = CYL-001.id)
   *   CYL-001-V2 (versioned, defaultCylinderId = CYL-001.id)
   *   CYL-002 (default)
   *   CYL-002-V1 (versioned, defaultCylinderId = CYL-002.id)
   *
   * WARNING: Used by filter() for search results. Do NOT change grouping logic
   * or search results will display incorrectly (all defaults then all versioned).
   *
   * @param subject - Array of cylinders to sort
   * @param flat - Flattened master key hierarchy for operator sorting
   * @param isOperators - True if sorting operators (different logic), false for cylinders
   * @returns Hierarchically sorted array
   */
  /* istanbul ignore next */
  sortHierarchically(subject, flat, isOperators) {
    const mapped = flat.map((s) => s.keySetSymbol)

    subject = subject.sort((a, b) => {
      return mapped.indexOf(a.keySetSymbol) - mapped.indexOf(b.keySetSymbol)
    })

    // Format cylinder operators
    if (isOperators) {
      return subject
        .sort((a, b) => b.isDefaultCylinder - a.isDefaultCylinder)
        .sort((a, b) => a.name.localeCompare(b.name, 'en', { numeric: true }))
    }

    // Format cylinders - optimized O(n) approach
    const defaultCylinders: any[] = []
    const versionedByDefaultId = new Map<number, any[]>()

    // Single pass: separate default and versioned cylinders
    for (const cyl of subject) {
      if (cyl.isDefaultCylinder) {
        defaultCylinders.push(cyl)
      } else if (cyl.defaultCylinderId) {
        const group = versionedByDefaultId.get(cyl.defaultCylinderId)
        if (group) {
          group.push(cyl)
        } else {
          versionedByDefaultId.set(cyl.defaultCylinderId, [cyl])
        }
      }
    }

    // Sort default cylinders by name
    defaultCylinders.sort((a, b) => a.name.localeCompare(b.name, 'en', { numeric: true }))

    // Build result: default cylinder followed by its versioned cylinders
    const sortedCyls: any[] = []
    for (const defaultCyl of defaultCylinders) {
      sortedCyls.push(defaultCyl)
      const versioned = versionedByDefaultId.get(defaultCyl.id)
      if (versioned) {
        versioned.sort((a, b) => a.name.localeCompare(b.name, 'en', { numeric: true }))
        sortedCyls.push(...versioned)
      }
    }

    return sortedCyls
  }

  /* istanbul ignore next */
  getPinningReport() {
    this.keyingSystemService
      .getPinningReport(this.structure.id, this.keySystem.id)
      .pipe(take(1))
      .subscribe(
        (res) => {
          // It is necessary to create a new blob object with mime-type explicitly set
          // otherwise only Chrome works like it should
          var newBlob = new Blob([res], { type: 'text/csv' })

          // IE doesn't allow using a blob object directly as link href
          // instead it is necessary to use msSaveOrOpenBlob
          if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
            ;(window.navigator as any).msSaveOrOpenBlob(newBlob)
            return
          }

          // For other browsers:
          // Create a link pointing to the ObjectURL containing the blob.
          const data = window.URL.createObjectURL(newBlob)

          var link = document.createElement('a')
          link.href = data
          link.download = `${this.structure.name}-MECS-${moment().format('MM-DD-YYYY')}`
          // this is necessary as link.click() does not work on the latest firefox
          link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }))

          setTimeout(() => {
            // For Firefox it is necessary to delay revoking the ObjectURL
            window.URL.revokeObjectURL(data)
            link.remove()
            this.loadingService.set({
              isLoading: false,
              message: '',
            })
          }, 100)
          this.loadingService.set({ isLoading: false, message: '' })
        },
        (error) => {
          this.errorService.throw(error)
        }
      )
  }

  /* istanbul ignore next */
  getAllCyls(filtered) {
    // Prevent duplicate API calls if one is already in progress
    if (this.cylinderLoadInProgress) {
      return
    }
    this.cylinderLoadInProgress = true

    let operatorForSort = true
    this.isLoadingCylinders = true
    // Loading is now handled by TokenizedMessageLoadingService in keying.cylinder.service

    // CrossKeyChild must always use the CrossKeyed filter so the API returns only
    // the cylinders that belong to that cross-keyed node.
    if (this.selectedMasterKey.masterKeyTypeId === KeyNodeType.CrossKeyChild) {
      this.filteredTypeCylinder = CylinderType.CrossKeyedCylinder
    }

    this.keyingCylinderService
      .getAllCylindersByMasterkeyidAndType(
        this.structure.id,
        this.keySystem.id,
        this.selectedMasterKey.id,
        this.filteredTypeCylinder
      )
      .pipe(take(1))
      .subscribe(
        (data) => {
          // For CrossKeyChild, always auto-select the cylinder immediately — do NOT guard
          // on selectedTabId here. CrossKeyChild always forces the Cylinders tab, so the
          // check is redundant. The double-click bug (first click showed an empty list,
          // second click opened the detail) was caused by generateShearlinesAndPinning()
          // early-returning for CrossKeyChild without ever calling childCylinders(), not
          // by a selectedTabId timing issue. That early-return is now fixed above.
          // Keeping selectedTabId === 1 here would be an unreliable guard anyway because
          // @Input() propagation is asynchronous relative to setSelectedMasterKey().
          // SelectedTab: 0 - Keys, 1 - Cylinders
          if (!this.clickedOnCylinderNav && this.selectedMasterKey.masterKeyTypeId === KeyNodeType.CrossKeyChild) {
            const _selectedMasterKey = data.find(
              (m) => m.masterKeyId === this.selectedMasterKey.id && m.isDefaultCylinder
            )
            this.selectCylinder(_selectedMasterKey || data[0])
          } else {
            let flat = this.selectedMasterKey.tmk().squashIncludeChangeKeys()

            this.allCylinders = data
            this.updateValidateCylindersState()
            let flatFiltered = flat.filter((mk) => !!mk)

            this.allCylinders.map((cyl) => {
              this.formatOperators(cyl, flatFiltered)
              cyl.keywayName = cyl.keyway?.name
              let mk = filtered.find((mk) => mk.id === cyl.masterKeyId)
              cyl.masterKeyTypeId = mk?.masterKeyTypeId
            })

            if (this.filteredTypeCylinder === CylinderType.AllCylinders) {
              operatorForSort = false
            }
            this.filteredAllCylinders = this.sortHierarchically(this.allCylinders, flat, operatorForSort)
            const ids = this.filteredAllCylinders.map((cyl) => cyl.id)

            this.filteredCylinderTypeCylinders = this.filterCylindersByCylinderType(this.filteredTypeCylinder)
            this.selectedCylindersCount = 0
          }
          this.isLoadingCylinders = false
          this.cylinderLoadInProgress = false
          // Loading is handled by TokenizedMessageLoadingService in keying.cylinder.service
          this.clickedOnCylinderNav = false
          this.cdr.markForCheck()
        },
        (error) => {
          this.isLoadingCylinders = false
          this.cylinderLoadInProgress = false
          this.cdr.markForCheck()
          // Error handling is done by keying.cylinder.service
        }
      )
  }

  formatOperators(cyl, flatFiltered) {
    let oppString = ''
    cyl.operators.map((opp) => {
      opp.masterKey = flatFiltered.find((mk) => mk.keySetSymbol === opp.name)
      if (opp.masterKey) {
        opp.keySetSymbol = opp.masterKey.keySetSymbol
      }
    })

    cyl.operators = this.sortHierarchically(cyl.operators, flatFiltered, true)

    cyl.operators.map((opp, index) => {
      if (!opp.name.includes('INSERT')) {
        if (index === 0 && cyl.operators.length > 1) {
          oppString = oppString + opp.name
        } else if (oppString) {
          oppString = oppString + ', ' + opp.name
        } else {
          oppString = oppString + opp.name
        }
      }

      cyl.oppString = oppString
    })
  }

  generateShearlinesAndPinning() {
    // Cylinder loading is handled by TokenizedMessageLoadingService in keying.cylinder.service
    this.keyingDataSetsService
      .getStructureDetails(this.structure.id)
      .pipe(take(1))
      .subscribe((structureDetails) => {
        this.keyingDataSetsService.structureDetails.next(structureDetails)
        if (this.selectedMasterKey.masterKeyTypeId === this.keyNodeType.CrossKeyChild) {
          // CrossKeyChild skips shearline/pinning generation entirely, but we still need
          // to load its cylinders here. Without this call, cylinder-library is created
          // fresh (via *ngIf) when the user first clicks a CrossKeyChild from the Keys tab,
          // and generateShearlinesAndPinning() would early-return before ever calling
          // childCylinders() — leaving the component empty or showing stale cylinders
          // from a prior BehaviorSubject emission. childCylinders() → getAllCyls() will
          // then trigger the auto-select logic to open the cylinder detail immediately.
          this.childCylinders()
          this.cdr.markForCheck()
          return
        }

        if (structureDetails.hasIndependentSystems) {
          if (
            structureDetails.totalCylindersWithNoPinning > 0 &&
            structureDetails.totalCylindersWithNoPinning !== structureDetails.totalCrossKeyedCylindersWithNoPinning
          ) {
            this.keyingSystemService
              .queueOfflinePinning(this.structure.id)
              .pipe(take(1))
              .subscribe((response) => {
                this.loggingService.log('Response:', response)
                /* Get Structure Details Everytime Queue Offline Pinning Generation */
                this.keyingDataSetsService.loadStructureDetails(this.structure.id)
                this.errorService.notify('Note', 'Pinning generation moved to offline processing.')
                this.cdr.markForCheck()
                // Loading cleanup is handled by loadStructureDetails
              })
          } else {
            this.childCylinders()
            // Loading state is handled by getAllCyls
          }
        } else if (!structureDetails.hasIndependentSystems) {
          if (
            structureDetails.totalCylindersWithNoPinning > 0 &&
            structureDetails.totalCylindersWithNoPinning !== structureDetails.totalCrossKeyedCylindersWithNoPinning &&
            structureDetails.totalCylindersWithNoPinning - structureDetails.totalCrossKeyedCylindersWithNoPinning < 200
          ) {
            this.loadingService.set({ isLoading: true, message: 'Generating Shearlines & Pinning' })
            this.keyingSystemService
              .generateShearlinesAndPinning(this.structure.id, this.keySystem.id)
              .pipe(take(1))
              .subscribe((response) => {
                this.loggingService.log('Response:', response)
                // Clear the loading state we set before calling tokenized methods
                this.loadingService.set({ isLoading: false, message: '' })
                /* Get Structure Details Everytime Pinning Generation Complete */
                this.keyingDataSetsService.loadStructureDetails(this.structure.id)
                this.childCylinders()
                this.cdr.markForCheck()
                // Loading state is handled by getAllCyls
              })
          } else if (
            structureDetails.totalCylindersWithNoPinning - structureDetails.totalCrossKeyedCylindersWithNoPinning >=
            200
          ) {
            this.loadingService.set({ isLoading: true, message: '' })
            this.keyingSystemService
              .queueOfflinePinning(this.structure.id)
              .pipe(take(1))
              .subscribe((response) => {
                this.loggingService.log('Response:', response)
                /* Get Structure Details Everytime Queue Offline Pinning Generation */
                this.keyingDataSetsService.loadStructureDetails(this.structure.id)
                this.errorService.notify('Note', 'Pinning generation moved to offline processing.')
                this.cdr.markForCheck()
                // Loading cleanup is handled by loadStructureDetails
              })
          } else {
            this.childCylinders()
            // Loading state is handled by getAllCyls
          }
        }
      })
  }

  filterCylindersByCylinderType(cylinerType: string) {
    switch (cylinerType) {
      case CylinderType.SelectedCylinders:
        return this.filteredAllCylinders.filter((cylinder) => cylinder.selected)

      default:
        return this.filteredAllCylinders
    }
  }

  /* istanbul ignore next */
  showUnverifiedCylinders() {
    this.filteredTypeCylinder = CylinderType.UnverifiedCylinder
    this.getAllCyls(this.filteredList)
  }

  /* istanbul ignore next */
  showSelectedCylinders() {
    this.filteredTypeCylinder = CylinderType.SelectedCylinders
    this.filteredCylinderTypeCylinders = this.filterCylindersByCylinderType(this.filteredTypeCylinder)
    this.cdr.markForCheck()
  }

  /* istanbul ignore next */
  showAllCylinders() {
    if (this.filteredTypeCylinder === CylinderType.SelectedCylinders) {
      this.filteredTypeCylinder = CylinderType.AllCylinders
      this.filteredCylinderTypeCylinders = this.filterCylindersByCylinderType(this.filteredTypeCylinder)
      this.cdr.markForCheck()
    } else {
      this.filteredTypeCylinder = CylinderType.AllCylinders
      // generateShearlinesAndPinning should only be called on component init (tab switch)
      // For filter changes, just fetch cylinders directly
      this.childCylinders()
    }
  }

  /* istanbul ignore next */
  showCrossKeyedCylinders() {
    this.filteredTypeCylinder = CylinderType.CrossKeyedCylinder
    this.getAllCyls(this.filteredList)
  }

  /* istanbul ignore next */
  prepareForMECSHandler() {
    this.prepareForMECS.emit(true)
  }

  /* istanbul ignore next */
  onValidateCylinders() {
    const eligibleCylinders: CylinderValidationItem[] = this.allCylinders
      .filter(
        (cyl) =>
          (cyl.cylinderTypeId === 2 || cyl.cylinderTypeId === 4) &&
          !cyl.requiredPinningGeneration &&
          !cyl.isReadyForMECS
      )
      .map((cyl) => ({ keyId: cyl.masterKeyId, cylinderId: cyl.id }))

    const payload: ValidateCylindersRequest = { cylinders: eligibleCylinders }

    this.keySystemService
      .validateCylinders(this.structure.id, this.keySystem.id, payload)
      .pipe(take(1))
      .subscribe({
        next: (message) => {
          this.errorService.notify('Success', message)
          this.refreshCylindersAfterValidation()
        },
        error: (error) => {
          this.errorService.throw(error)
          this.cdr.markForCheck()
        },
      })
  }

  /* istanbul ignore next */
  private refreshCylindersAfterValidation() {
    this.isLoadingCylinders = true
    this.keyingCylinderService
      .getAllCylindersByMasterkeyidAndType(
        this.structure.id,
        this.keySystem.id,
        this.selectedMasterKey.id,
        this.filteredTypeCylinder
      )
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          const flat = this.selectedMasterKey.tmk().squashIncludeChangeKeys()
          const flatFiltered = flat.filter((mk) => !!mk)
          this.allCylinders = data
          this.allCylinders.forEach((cyl) => {
            this.formatOperators(cyl, flatFiltered)
            cyl.keywayName = cyl.keyway?.name
            const mk = this.filteredList.find((mk) => mk.id === cyl.masterKeyId)
            cyl.masterKeyTypeId = mk?.masterKeyTypeId
          })
          // Set backing field directly to bypass setter's cross-action auto-select logic
          this._filteredAllCylinders = this.sortHierarchically(this.allCylinders, flat, true) || []
          this.filteredCylinderTypeCylinders = this.filterCylindersByCylinderType(this.filteredTypeCylinder)
          this.selectedCylindersCount = 0
          this.isLoadingCylinders = false
          this.updateValidateCylindersState()
          this.cdr.markForCheck()
        },
        error: () => {
          this.isLoadingCylinders = false
          this.cdr.markForCheck()
        },
      })
  }

  private updateValidateCylindersState() {
    const hasEligible = this.allCylinders.some(
      (cyl) =>
        (cyl.cylinderTypeId === 2 || cyl.cylinderTypeId === 4) &&
        !cyl.requiredPinningGeneration &&
        !cyl.isReadyForMECS
    )
    this.validateCylindersDisabled = !hasEligible
    this.cdr.markForCheck()
  }

  private notifyDuplicateSymbols(): void {
    this.keySystemService
      .getDuplicateSymbols(this.structure.id, this.keySystem.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((duplicateSymbols) => {
        const keySymbols = duplicateSymbols.keySymbols
        const cylinderSymbols = duplicateSymbols.cylinderSymbols

        if (keySymbols && keySymbols.length > 0) {
          const keySymbolsList = keySymbols.join(', ')
          this.errorService.showError(`Duplicate Key Symbols found: ${keySymbolsList}`)
        }

        if (cylinderSymbols && cylinderSymbols.length > 0) {
          const cylinderSymbolsList = cylinderSymbols.join(', ')
          this.errorService.showError(`Duplicate Cylinder Symbols found: ${cylinderSymbolsList}`)
        }
        this.cdr.markForCheck()
      })
  }
}
